import {
  DEALER_GROUP_CATEGORY,
  ENGLISH,
  LINCOLN_ENGLISH,
} from "../../common/Constants";

/**
 * Shared logic for changing dealer brand (Ford/Lincoln).
 * Mutates user in place and returns the updated user plus the language to set for USA.
 *
 * Used by EcommDealerOnboardingApp (via useChangeBrand), NADealerApp, and FieldViewApp.
 *
 * @param {import("./NADealer").default} user - NADealer instance (mutated in place)
 * @param {string} brandCode - "F" or "L"
 * @param {Object} options
 * @param {boolean} options.isUsaMarket - from IS_USA_MARKET(user.market)
 * @param {boolean} [options.includeFordHubMdlr=true] - when F, also switch to HUB/MDLR dealer for "show all"
 * @param {boolean} [options.includeLanguage=true] - when USA, return language to set
 * @returns {{ updatedUser: import("./NADealer").default, language?: string }}
 */
export function applyBrandChange(user, brandCode, options = {}) {
  const {
    isUsaMarket,
    includeFordHubMdlr = true,
    includeLanguage = true,
  } = options;

  user.updateBrand(brandCode);

  const hasValidDealerGroup = user?.dealerGroup?.length > 0;

  if (hasValidDealerGroup) {
    updateDealerSelection(user, brandCode, isUsaMarket, includeFordHubMdlr);
  }

  const language = determineLanguage(includeLanguage, isUsaMarket, brandCode);

  return { updatedUser: user, language };
}

function updateDealerSelection(user, brandCode, isUsaMarket, includeFordHubMdlr) {
  const dealer = findDealerByBrand(user.dealerGroup, brandCode, isUsaMarket);

  if (!dealer) {
    return;
  }

  user.updateCurrentDealer(dealer, false);

  if (shouldUpdateToHubOrMdlr(brandCode, includeFordHubMdlr)) {
    updateToHubOrMdlr(user);
  }
}

function findDealerByBrand(dealerGroup, brandCode, isUsaMarket) {
  if (isUsaMarket) {
    const byBrand = dealerGroup.filter((d) => d.salesCode?.startsWith(brandCode));
    if (byBrand.length === 0) return undefined;
    const hasCategory = byBrand.some(
      (d) =>
        d.dealerLocationCategory != null &&
        d.dealerLocationCategory !== undefined
    );
    if (hasCategory) {
      return (
        byBrand.find(
          (d) => d.dealerLocationCategory === DEALER_GROUP_CATEGORY.PRIMARY
        ) ||
        byBrand.find(
          (d) => d.dealerLocationCategory === DEALER_GROUP_CATEGORY.SECONDARY
        ) ||
        byBrand.find(
          (d) => d.dealerLocationCategory === DEALER_GROUP_CATEGORY.OTHER
        ) ||
        byBrand[0]
      );
    }
    return byBrand[0];
  }
  return dealerGroup.find((d) => d.commonId?.endsWith(brandCode));
}

function shouldUpdateToHubOrMdlr(brandCode, includeFordHubMdlr) {
  return includeFordHubMdlr && brandCode === "F";
}

function updateToHubOrMdlr(user) {
  const hubOrMdlr = user.dealerGroup.find(
    (d) => d.dealerType === "HUB" || d.dealerType === "MDLR"
  );

  if (hubOrMdlr) {
    user.updateCurrentDealer(hubOrMdlr, true);
  }
}

function determineLanguage(includeLanguage, isUsaMarket, brandCode) {
  if (!includeLanguage || !isUsaMarket) {
    return undefined;
  }

  return brandCode === "L" ? LINCOLN_ENGLISH : ENGLISH;
}
